import {
    Box, Drawer, MenuItem, Switch,
    TextField,
    CircularProgress,
    Grid
} from "@mui/material";
import { Button, FormControlLabel, FormGroup } from "@material-ui/core";
import { useCallback, useEffect, useMemo, useRef } from "react";
import React from "react";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AutomationSetting, DepartmentPhoneResponse, DepartmentResponse, GetWorkOrderStatusesResponse } from '../../api/automation-api';
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import TextSettings from './text-settings';
import EmailSettings from './email-settings';
import TaskSettings from './task-settings';
import { AutomationConstant } from "components/automation/api/automation-constants";
import DrawerToolbarWithActions from "common/components/drawer/drawer-toolbar-with-actions";
import { TaskType } from "components/lead/models/task";
import { formatPhoneNumber } from "common/utils";
import { DmsTypeEnum } from 'common/enums/DmsTypeEnum';
import { useAppSelector } from "app";

const fieldNames = {
    dealerId: "dealerId",
    departmentId: "departmentId",
    statusId: "statusId",
    isActive: "isActive",
    isTask: "isTask",
    isText: "isText",
    isEmail: "isEmail",
    isStatusCode: "isStatusCode"    
}
const commonTextFieldProps = {
    fullWidth: true,
    variant: "outlined" as const,
};
interface AddEditAutomationProps {
    isOpen: boolean;
    initialValues?: AutomationSetting | null;
    allLocations: any;
    dataWOStatus: Array<GetWorkOrderStatusesResponse> | undefined;
    allDepartments: Array<DepartmentResponse>;
    allTaskTypes: TaskType[];
    allUsers: any;
    addUpdateLoading: boolean;
    deleteBtnLoading: boolean;
    onSubmit: (values: any) => Promise<void>;
    onClose: () => void;
    onStatusCodeToggle?: (isStatusCode: boolean) => void;
    isExistingStatus: (value: string, dealerId: number) => boolean;
    DeleteAutomationSetting(id: number): Promise<void>;
}

const AddEditAutomation: React.FC<AddEditAutomationProps> = ({
    isOpen = false,
    initialValues,
    allLocations,
    dataWOStatus,
    allDepartments,
    allTaskTypes,
    allUsers,
    addUpdateLoading,
    deleteBtnLoading,
    onSubmit,
    onClose,
    onStatusCodeToggle,
    isExistingStatus,
    DeleteAutomationSetting
}) => {

    const isNewItem = !initialValues?.id;

    const taskFormRef = useRef<FormikProps<any> | null>(null);
    const emailFormRef = useRef<FormikProps<any> | null>(null);
    const textFormRef = useRef<FormikProps<any> | null>(null);
    const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);

    const defaultValues = useMemo(() => {
        const firstLocation = allLocations?.[0]?.dealerLocationId ?? 0;
        const firstDepartment = allDepartments
            ?.find((dept: any) => dept.dealerLocationId === firstLocation)
            ?.Departments?.[0]?.departmentId ?? 0;

        const selectedLocation = allLocations?.find((loc: any) => loc.dealerLocationId === (isNewItem ? firstLocation : initialValues?.dealerId));

        return {
            id: initialValues?.id ?? 0,
            dealerId: isNewItem ? firstLocation : initialValues?.dealerId ?? 0,
            departmentId: isNewItem ? firstDepartment : initialValues?.departmentId ?? 0,
            locationName: selectedLocation?.name ?? "",
            departmentName: initialValues?.departmentName ?? "",
            orderStatus: initialValues?.orderStatus ?? "",
            orderStatusId: initialValues?.orderStatusId ?? "",
            delayMessage: initialValues?.delayMessage ?? 0,

            startingHour: initialValues?.startingHour ?? 8,
            endingHour: initialValues?.endingHour ?? 18,

            isTask: initialValues?.isTask ?? false,
            isText: initialValues?.isText ?? false,
            isEmail: initialValues?.isEmail ?? false,
            isSurvey: initialValues?.isSurvey ?? false,
            isStatusCode: initialValues?.isStatusCode ?? false,            

            isActive: initialValues?.isActive ?? false,
            createdBy: initialValues?.createdBy ?? "",
            creationDate: initialValues?.creationDate ?? new Date(),
            updatedBy: initialValues?.updatedBy ?? "",
            updationDate: initialValues?.updationDate ?? null,
        };

    }, [initialValues, allLocations, allDepartments]);

    const validationSchema = useMemo(() => Yup.object<any>().shape<any>({
        dealerId: Yup.number().required("Location is required").moreThan(0, "Location is required"),
        departmentId: Yup.number().required("Department is required").moreThan(0, "Department is required"),
        orderStatusId: Yup.string().trim()
            .required("Status is required")
            .test(
                "unique-status",
                "Status already exists",
                function (value) {
                    const { dealerId, id = 0 } = this.parent;

                    if (id === 0) {
                        return !isExistingStatus(value || "", dealerId);
                    }

                    return true;
                }
            ),
        isActive: Yup.boolean(),
    }), []);

    const formik = useFormik({
        initialValues: defaultValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {

            if (addUpdateLoading || deleteBtnLoading) {
                return;
            }

            try {

                await Promise.all([
                    taskFormRef.current?.submitForm(),
                    emailFormRef.current?.submitForm(),
                    textFormRef.current?.submitForm(),
                ]);

                const isChildValid = checkChildSettingsValid();

                if (formik.isValid && isChildValid) {

                    const allData = {
                        ...formik.values,
                        ...taskFormRef.current?.values,
                        ...emailFormRef.current?.values,
                        ...textFormRef.current?.values,
                    };

                    onSubmit(allData);

                }
            } catch (error) {
            }
        },
    });

    const checkChildSettingsValid = (): boolean => {
        if (formik.values.isTask) {
            if (!taskFormRef.current?.isValid)
                return false;
        }
        if (formik.values.isEmail) {
            if (!emailFormRef.current?.isValid)
                return false;
        }
        if (formik.values.isText) {
            if (!textFormRef.current?.isValid)
                return false;
        }

        return true;
    }
    const initialTaskValues = useMemo(() => {
        return {
            isTask: formik.values.isTask,
            taskSubject: initialValues?.taskSubject || AutomationConstant.DefaultTaskSubject,
            taskTypeId: initialValues?.taskTypeId??-1,
            taskDueDays: initialValues?.taskDueDays ?? 0,
            taskNotes: initialValues?.taskNotes || "",
            assignedUserId: initialValues?.assignedUserId || "",
        };
    }, [formik.values.isTask, initialValues]);

    const initialEmailValues = useMemo(() => {
        return {
            isEmail: formik.values.isEmail,
            emailSubject: initialValues?.emailSubject || "",
            emailMessage: initialValues?.emailMessage || AutomationConstant.DefaultMessage,
            emailPreHeader: initialValues?.emailPreHeader || "",
        };
    }, [formik.values.isEmail, initialValues]);

    const initialTextValues = useMemo(() => {
        return {
            isText: formik.values.isText,
            textMessage: initialValues?.textMessage || AutomationConstant.DefaultMessage,
        };
    }, [formik.values.isText, initialValues]);

    const filteredTaskTypes = useMemo(() => {
        const departmentId = formik.values.departmentId;
        return allTaskTypes.filter(task =>
            task.departments?.some(dept => dept.departmentId === departmentId)
          );
    }, [allTaskTypes, formik.values.departmentId]);

    const filteredUsers = useMemo(() => {
        const departmentId = formik.values.departmentId;
        return allUsers?.filter((user: any) =>
            user.departments.some((dept: any) => dept.departmentId === departmentId)
        );
    }, [allUsers, formik.values.departmentId]);

    const locationOptions = useMemo(() => {
        return allLocations?.map((options: any) => (
            <MenuItem key={options.dealerLocationId} value={options.dealerLocationId}>
                {options.name}
            </MenuItem>
        ));
    }, [allLocations]);

    const departmentOptions = useMemo(() => {
        return (
            allDepartments
                ?.find((item: DepartmentResponse) => item.dealerLocationId == formik.values.dealerId)
                ?.Departments?.map((options: DepartmentPhoneResponse) => (
                    <MenuItem key={options.departmentId} value={options.departmentId}>
                        {options.departmentName} {formatPhoneNumber(options.phoneNumber)}
                    </MenuItem>
                )) || []
        );
    }, [allDepartments, formik.values.dealerId]);

    const statusOptions = useMemo(() => {
        return dataWOStatus?.map((options: GetWorkOrderStatusesResponse) => (
            <MenuItem key={options.jobStatusId} value={options.jobStatusId}>
                {options.jobStatus}
            </MenuItem>
        ));
    }, [dataWOStatus]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleStatusCodeToggle = useCallback((nextIsStatusCode: boolean) => {
        formik.setFieldValue(fieldNames.isStatusCode, nextIsStatusCode);
        onStatusCodeToggle?.(nextIsStatusCode);
    }, [formik, onStatusCodeToggle]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        onStatusCodeToggle?.(formik.values.isStatusCode);
    }, [isOpen, formik.values.isStatusCode, onStatusCodeToggle]);

    return (
        <Box>
            <Drawer
                PaperProps={{ style: { width: 'auto', minWidth: "50%" } }}
                anchor="right"
                open={isOpen}
                onClose={() => {
                    if (!addUpdateLoading && !deleteBtnLoading) handleClose();
                }}
                variant='temporary'
            >
                <Box>
                    <DrawerToolbarWithActions
                        title={isNewItem ? "New Automation" : "Edit Automation"}
                        onClose={() => {
                            if (!addUpdateLoading && !deleteBtnLoading) handleClose();
                        }}
                        actions={
                            <>
                                <Button
                                    onClick={() => { formik.handleSubmit() }}
                                    variant="contained"
                                    startIcon={!addUpdateLoading ? <EditIcon /> : null}
                                    color="primary"
                                    disabled={formik.isSubmitting || addUpdateLoading || deleteBtnLoading}
                                >
                                    {addUpdateLoading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        `Save`
                                    )}
                                </Button>

                                {formik.values.id > 0 && (
                                    <Button
                                        onClick={() => {
                                            if (!deleteBtnLoading) {
                                                DeleteAutomationSetting(formik.values.id);
                                            }
                                        }}
                                        color="secondary"
                                        startIcon={!deleteBtnLoading ? <DeleteIcon /> : null}
                                        variant="contained"
                                        disabled={deleteBtnLoading || addUpdateLoading}
                                        style={{ marginLeft: "10px" }}
                                    >
                                        {deleteBtnLoading ? <CircularProgress size={24} color="inherit" /> : "Delete"}
                                    </Button>
                                )}

                            </>
                        }
                        middleActions={ <FormControlLabel
                            control={
                                <Switch
                                    id="settings-isActive"
                                    checked={formik.values.isActive}
                                    onChange={(e) => formik.setFieldValue(fieldNames.isActive, e.target.checked)}
                                    name={fieldNames.isActive}
                                    color="primary"
                                />
                            }
                            label="Active"
                        />}
                    />

                    <Box style={{ margin: "10px 12px" }}>
                        <Grid container spacing={2} alignItems="flex-start" >

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                   {...commonTextFieldProps}
                                    name={fieldNames.dealerId}
                                    placeholder="Location"
                                    label="Location"
                                    size="small"
                                    disabled={!isNewItem}
                                    value={formik.values.dealerId}
                                    onChange={(event) => {
                                        const selectedDealerId = Number(event.target.value);
                                        const selectedLocation = allLocations?.find((loc: any) => loc.dealerLocationId === selectedDealerId);
                                        const selectedDepartment = allDepartments
                                            ?.find((item: DepartmentResponse) => item.dealerLocationId === selectedDealerId)
                                            ?.Departments?.[0]?.departmentId || 0;

                                        formik.setValues({
                                            ...formik.values,
                                            dealerId: selectedDealerId,
                                            locationName: selectedLocation?.name || "",
                                            departmentId: selectedDepartment
                                        });
                                    }}
                                    error={!!formik.errors.dealerId}
                                    helperText={formik.errors.dealerId}
                                >
                                    {locationOptions}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    {...commonTextFieldProps}
                                    name={fieldNames.departmentId}
                                    placeholder="Department"
                                    label="Department"
                                    size="small"
                                    value={formik.values.departmentId}
                                    onChange={(event) => {
                                        const selectedDepartment = Number(event.target.value);
                                        formik.setFieldValue(fieldNames.departmentId, selectedDepartment);

                                        const currentTaskTypeId = taskFormRef?.current?.values?.taskTypeId;
                                        const validTask = allTaskTypes.find(task =>
                                                    task.id === currentTaskTypeId &&
                                                    task.departments.some(dept => dept.departmentId === Number(selectedDepartment))
                                                    );

                                        taskFormRef?.current?.setValues({
                                            ...taskFormRef?.current?.values,
                                            assignedUserId: "",
                                            taskTypeId: validTask?.id|| -1,
                                                 
                                        });
                                    }}
                                    error={!!formik.errors.departmentId}
                                    helperText={formik.errors.departmentId}
                                >
                                    {departmentOptions}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                {typeof (dataWOStatus) !== 'undefined' && (

                                    <TextField
                                        select
                                        {...commonTextFieldProps}
                                        name={fieldNames.statusId}
                                        placeholder="Trigger"
                                        label="Trigger"
                                        size="small"
                                        disabled={!isNewItem}
                                        value={formik.values.orderStatusId}
                                        onChange={(event) => {
                                            const selectedStatusId = event.target.value;
                                            const selectedStatus = dataWOStatus?.find(status => status.jobStatusId === selectedStatusId);
                                            formik.setValues({
                                                ...formik.values,
                                                orderStatusId: selectedStatusId,
                                                orderStatus: selectedStatus?.jobStatus || "",
                                            });
                                        }}
                                        error={!!formik.errors.orderStatusId}
                                        helperText={formik.errors.orderStatusId}
                                    >
                                        {statusOptions}
                                    </TextField>

                                )}
                            </Grid>

                        </Grid>
                    </Box>

                    <Box style={{ margin: "5px 12px" }}>
                        <FormGroup row>
                            <FormControlLabel control={
                                <Switch
                                    id="settings-isTask"
                                    checked={formik.values.isTask}
                                    onChange={(e) => formik.setFieldValue(fieldNames.isTask, e.target.checked)}
                                    name={fieldNames.isTask}
                                    color="primary" />
                            }
                                label="Task" />

                            <FormControlLabel control={<Switch
                                id="settings-isText"
                                checked={formik.values.isText}
                                onChange={(e) => formik.setFieldValue(fieldNames.isText, e.target.checked)}
                                name={fieldNames.isText}
                                color="primary"
                            />} label="Text" />


                            <FormControlLabel control={<Switch
                                id="settings-isEmail"
                                checked={formik.values.isEmail}
                                onChange={(e) => formik.setFieldValue(fieldNames.isEmail, e.target.checked)}
                                name={fieldNames.isEmail}
                                color="primary"
                            />} label="Email" />

                    {dmsTypeId === DmsTypeEnum.DIS && (
                            <FormControlLabel control={<Switch
                                id="settings-isStatusCode"
                                checked={formik.values.isStatusCode}
                                onChange={(e) => handleStatusCodeToggle(e.target.checked)}
                                name={fieldNames.isStatusCode}
                                color="primary"
                                disabled={!isNewItem} 
                            />} label="Status Code" />
                    )}
                        </FormGroup>
                    </Box>

                    <Box style={{ margin: "5px 12px" }}>

                        {formik.values.isTask && <TaskSettings
                            ref={taskFormRef}
                            initialValues={initialTaskValues}
                            allTaskTypes={filteredTaskTypes}
                            filteredUsers={filteredUsers}
                        />}

                        {formik.values.isText && <TextSettings
                            ref={textFormRef}
                            initialValues={initialTextValues}
                        />}

                        {formik.values.isEmail && <EmailSettings
                            ref={emailFormRef}
                            initialValues={initialEmailValues}
                        />}

                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

export default AddEditAutomation;








